import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { toast } from "sonner";
import type { MedicalTextIssue, CreateIssueData } from "@/types/issues";
import { useEffect } from "react";

export const useMedicalTextIssues = (medicalTextExampleId?: string) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Fetch issues for a medical text example
  const {
    data: issues,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["medical-text-issues", medicalTextExampleId],
    queryFn: async () => {
      if (!medicalTextExampleId) return [];

      console.log("Fetching issues for medical text example:", medicalTextExampleId);

      const { data, error } = await supabase
        .from("medical_text_issues")
        .select(
          `
          *,
          created_by_profile:profiles!medical_text_issues_created_by_fkey(display_name, display_color),
          resolved_by_profile:profiles!medical_text_issues_resolved_by_fkey(display_name, display_color)
        `,
        )
        .eq("medical_text_example_id", medicalTextExampleId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching issues:", error);
        throw error;
      }

      console.log("Fetched issues:", data);
      return data as MedicalTextIssue[];
    },
    enabled: !!medicalTextExampleId,
  });

  // Create new issue mutation
  const createIssueMutation = useMutation({
    mutationFn: async (data: CreateIssueData) => {
      console.log("useMedicalTextIssues: Creating issue mutation with data:", data);

      if (!session?.user?.id) throw new Error("User not authenticated");

      const { data: newIssue, error } = await supabase
        .from("medical_text_issues")
        .insert({
          ...data,
          created_by: session.user.id,
        })
        .select(
          `
          *,
          created_by_profile:profiles!medical_text_issues_created_by_fkey(display_name, display_color),
          resolved_by_profile:profiles!medical_text_issues_resolved_by_fkey(display_name, display_color)
        `,
        )
        .single();

      if (error) {
        console.error("Error creating issue:", error);
        throw error;
      }

      console.log("Issue created successfully:", newIssue);
      return newIssue;
    },
    onSuccess: async (newIssue) => {
      console.log("useMedicalTextIssues: Issue creation successful, invalidating queries");

      // Invalidate and refetch the issues query
      await queryClient.invalidateQueries({
        queryKey: ["medical-text-issues", medicalTextExampleId],
      });

      // Also manually update the cache to ensure immediate UI update
      queryClient.setQueryData(
        ["medical-text-issues", medicalTextExampleId],
        (oldData: MedicalTextIssue[] | undefined) => {
          if (!oldData) return [newIssue];
          return [newIssue, ...oldData];
        },
      );

      toast.success("Issue created successfully");
    },
    onError: (error) => {
      console.error("useMedicalTextIssues: Issue creation failed:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create issue");
    },
  });

  // Resolve issue mutation
  const resolveIssueMutation = useMutation({
    mutationFn: async (issueId: string) => {
      if (!session?.user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("medical_text_issues")
        .update({
          status: "resolved",
          resolved_by: session.user.id,
          resolved_at: new Date().toISOString(),
        })
        .eq("id", issueId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medical-text-issues", medicalTextExampleId],
      });
      toast.success("Issue resolved successfully");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to resolve issue");
    },
  });

  // Reopen issue mutation
  const reopenIssueMutation = useMutation({
    mutationFn: async (issueId: string) => {
      const { data, error } = await supabase
        .from("medical_text_issues")
        .update({
          status: "active",
          resolved_by: null,
          resolved_at: null,
        })
        .eq("id", issueId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medical-text-issues", medicalTextExampleId],
      });
      toast.success("Issue reopened successfully");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to reopen issue");
    },
  });

  // Real-time subscription
  useEffect(() => {
    if (!medicalTextExampleId) return;

    console.log("Setting up real-time subscription for medical text example:", medicalTextExampleId);

    const channel = supabase
      .channel("medical-text-issues-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "medical_text_issues",
          filter: `medical_text_example_id=eq.${medicalTextExampleId}`,
        },
        (payload) => {
          console.log("Real-time change detected:", payload);
          queryClient.invalidateQueries({
            queryKey: ["medical-text-issues", medicalTextExampleId],
          });
        },
      )
      .subscribe();

    return () => {
      console.log("Cleaning up real-time subscription");
      supabase.removeChannel(channel);
    };
  }, [medicalTextExampleId, queryClient]);

  // Create issue function that supports callbacks
  const createIssue = (
    data: CreateIssueData,
    callbacks?: { onSuccess?: (data: any) => void; onError?: (error: any) => void },
  ) => {
    console.log("useMedicalTextIssues: createIssue called with data:", data);

    createIssueMutation.mutate(data, {
      onSuccess: (result) => {
        console.log("useMedicalTextIssues: Mutation success, calling callback");
        callbacks?.onSuccess?.(result);
      },
      onError: (error) => {
        console.log("useMedicalTextIssues: Mutation error, calling callback");
        callbacks?.onError?.(error);
      },
    });
  };

  return {
    issues: issues || [],
    isLoading,
    error,
    createIssue,
    isCreatingIssue: createIssueMutation.isPending,
    resolveIssue: resolveIssueMutation.mutate,
    isResolvingIssue: resolveIssueMutation.isPending,
    reopenIssue: reopenIssueMutation.mutate,
    isReopeningIssue: reopenIssueMutation.isPending,
  };
};
